import { FeatureFlagIdRequestSchema } from 'c1gen/features'
import { C1Chip, C1ChipProps } from 'c1ui/components/C1Chip'
import { getOutcome, taskHasDeniedOutcome } from 'components/core/task/outcome/getOutcomeText'
import { useFeatureChecker } from 'components/no-ui/capability'
import { TaskStatusFormattedMessage } from 'components/pages/task/details/content/TaskFormattedMessage'
import { canActOn } from 'components/pages/task/state'
import { TaskValue } from 'lib/actions/task'
import { SystemUserID } from 'lib/constants/system'
import { UserContext } from 'lib/context/app'
import { ActionOutcome, CertifyOutcome, GrantOutcome, RevokeOutcome, TaskActionType } from 'pbts/c1/api/task/v1/task'
import { FC, useContext } from 'react'

type TaskStatusLabelProps = {
    task: TaskValue
    size?: C1ChipProps['size']
}

export const useGetUserCanActOnTask = (task: TaskValue): boolean => {
    const userContext = useContext(UserContext)
    const hasRequestSchemaFeature = useFeatureChecker(FeatureFlagIdRequestSchema)

    // if its a form and your the subject, approver, or superadmin
    const isForm = task.policy?.current?.instance.oneofKind === 'form'
    const canActOnFormStep = canActOn(task, TaskActionType.UPDATE_REQUEST_DATA)
    const isFormAndCanActOn = isForm && canActOnFormStep && (hasRequestSchemaFeature ?? false)

    return userContext?.user?.id ? task.stepApproverIds.includes(userContext.user.id) || isFormAndCanActOn : false
}

export const getTaskIsAssignedToSystemUser = (task: TaskValue): boolean => {
    return task.stepApproverIds.length === 1 && task.stepApproverIds[0] === SystemUserID
}

export const useGetTaskStatusLabelColor = (task: TaskValue): Exclude<C1ChipProps['color'], 'default' | 'secondary'> => {
    const outcome = getOutcome(task)
    const userCanActOnTask = useGetUserCanActOnTask(task)

    const currentStep = task.policy?.current

    const someoneElseCanActOnTask = task.stepApproverIds.length > 0 && !userCanActOnTask
    const taskIsAssignedToSystemUser = getTaskIsAssignedToSystemUser(task)
    const noOneIsAssignedToTask = task.stepApproverIds.length === 0
    const deniedOutcome = taskHasDeniedOutcome(task)

    // First, check if the task is open
    if (currentStep && !deniedOutcome) {
        // If the task is open, and the user can act on it, use warning (orange)
        if (userCanActOnTask) {
            return 'warning'
        }

        // If the user can't act on the task, but someone else can, use primary (blue),
        // unless the task is assigned to the system user, in which case use info (grey).
        if (someoneElseCanActOnTask) {
            if (taskIsAssignedToSystemUser) {
                return 'info'
            }

            return 'primary'
        }

        // If no one is assigned to the task, use info (grey)
        if (noOneIsAssignedToTask) {
            return 'info'
        }
    }

    // Next, check if the task has an outcome
    switch (outcome) {
        // Tasks with a positive outcome have a success (green) color
        case CertifyOutcome.CERTIFIED:
        case GrantOutcome.GRANTED:
        case RevokeOutcome.REVOKED:
        case ActionOutcome.SUCCESS:
            return 'success'
        // Tasks with a negative outcome have an error (red) color
        case CertifyOutcome.DECERTIFIED:
        case GrantOutcome.DENIED:
        case RevokeOutcome.DENIED:
        case ActionOutcome.DENIED:
            return 'error'
        // Tasks with an error outcome have also have an error (red) color
        case CertifyOutcome.ERROR:
        case GrantOutcome.ERROR:
        case RevokeOutcome.ERROR:
        case ActionOutcome.ERROR:
            return 'error'
        // Cancelled tasks have an info (grey) color
        case CertifyOutcome.CANCELLED:
        case GrantOutcome.CANCELLED:
        case RevokeOutcome.CANCELLED:
        case ActionOutcome.CANCELLED:
            return 'info'
        // Tasks that have timed out have an error (red) color
        case GrantOutcome.WAIT_TIMED_OUT:
        case RevokeOutcome.WAIT_TIMED_OUT:
        case CertifyOutcome.WAIT_TIMED_OUT:
            return 'error'
        default:
    }

    // If we can't figure out the color, default to info (grey)
    console.warn('No color found for task', task)
    return 'info'
}

export const TaskStatusLabel: FC<TaskStatusLabelProps> = ({ task, size = 'small' }) => {
    const color = useGetTaskStatusLabelColor(task)

    return <C1Chip color={color} label={<TaskStatusFormattedMessage task={task} />} size={size} />
}
